import { css } from 'styled-components';
import { stripUnit } from 'polished';
import { SDS_SIZE_BORDER_LARGE, SDS_SIZE_XSMALL } from '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { SDS_SPACE_XXXXSMALL, SDS_SPACE_XXXSMALL } from '@s-group/design-system-tokens/web/tokens/space.es6.js';

const linkColor = ({ color, theme }) => color === 'grey'
    ? theme.colors.SDS_BRAND_COLOR_TEXT_DEFAULT_GREY
    : theme.colors.SDS_BRAND_COLOR_TEXT_STRONG_PRIMARY;
// Logic for link icon positioning:
// 1. By default the bottom of the icon asset is aligned with the baseline of
//    the link text.
//    (See: https://www.w3.org/TR/css-values-4/#font-relative-lengths)
// 2. According to the design specification, the icon asset contains 2px
//    padding in its original size, which is in this case 16px.
//    (See: https://docs.designsystem.s-cloud.fi/docs/design-tokens/icons#sizing)
// 3. To align the bottom of the visible icon graphic with the baseline of
//    the text the 2px padding needs to be subtracted.
// 4. Due 1.5px line width in the font and in the icons and sub-pixel rendering
//    the resulted icon vertical position is still slightly too high.
//    Therefore, an additional 0.5px adjustment is made.
// 5. As the icon is scaled with the font-size, the position adjustment is
//    made using em unit, where 16px = 1em.
const iconBaselineAdjustment = `${(-2 - 0.5) / 16}em`;
const LinkStyle = css `
    color: ${linkColor};
    display: ${({ standalone }) => (standalone ? 'block' : 'inline')};
    text-decoration: ${({ standalone }) => !standalone ? 'underline' : 'none'};

    &:hover {
        text-decoration: ${({ standalone }) => standalone ? 'underline' : 'none'};
    }

    &:active {
        color: ${({ theme }) => theme.colors.SDS_BRAND_COLOR_TEXT_MEDIUM_PRIMARY};
    }

    &:active,
    &:focus {
        outline: ${({ theme }) => theme.colors.SDS_BRAND_COLOR_BORDER_STRONG_PRIMARY}
            solid ${SDS_SIZE_BORDER_LARGE};
        outline-offset: ${SDS_SPACE_XXXXSMALL};
    }

    &:visited {
        color: ${({ theme }) => theme.colors.SDS_BRAND_COLOR_TEXT_STRONG_GREY};
    }

    /* basic icon styles for both external and standalone icons */
    .icon-base {
        color: ${({ theme }) => theme.colors.SDS_BRAND_ELEMENT_STRONG_PRIMARY};
        display: inline;
        padding-left: ${({ iconPos, standalone, external }) => external
    ? SDS_SPACE_XXXXSMALL
    : iconPos === 'after'
        ? standalone
            ? SDS_SPACE_XXXSMALL
            : SDS_SPACE_XXXXSMALL
        : ''};
        padding-right: ${({ iconPos, standalone, external }) => external
    ? SDS_SPACE_XXXXSMALL
    : iconPos === 'before'
        ? standalone
            ? SDS_SPACE_XXXSMALL
            : SDS_SPACE_XXXXSMALL
        : ''};

        &:visited {
            color: ${({ theme }) => theme.colors.SDS_BRAND_ELEMENT_STRONG_GREY};
        }

        svg {
            height: ${stripUnit(SDS_SIZE_XSMALL)}em;
            vertical-align: ${iconBaselineAdjustment};
            width: ${stripUnit(SDS_SIZE_XSMALL)}em;

            /* Media query is used to detect support for lh unit (line-height), 
             * currently only available in Chrome 109 and newer.
             * https://caniuse.com/?search=lh%20unit
             */
            @media (min-height: 1lh) {
                margin: calc((1lh - ${stripUnit(SDS_SIZE_XSMALL)}em) / 2) 0;
                vertical-align: bottom;
            }
        }
    }
`;

export { LinkStyle };
//# sourceMappingURL=Link.styles.js.map
